import React, { createContext, useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { RouteComponentProps } from 'react-router';

import { Box } from '@chakra-ui/react';
import { Loading, MultipleAccordion } from 'components';
import { IAccordion } from 'constants/MultipleAcorddions';
import {
  Agents,
  Conclusions,
  DataNature,
  Impacts,
  PersonalData,
  Purposes,
  TechnicalAspects,
  TreatmentAspects,
} from './components/Forms';
import { openNotification } from 'utils/notification';
import { readEvaluation, readEvaluationDetail, save, sendEmail } from 'services/evaluationProject';
import Evaluation, { IEvaluationProjectCompleto } from 'models/Forms/Evaluation';
import useEventBus from 'hooks/useEventBus';
import { getEvaluationFinishPosition } from 'services/evaluationConclusion';

type stClient = (d: Evaluation) => Evaluation;

export const evaluationCtx = createContext({
  data: new Evaluation(),
  isDetails: false,
  results: undefined as IEvaluationProjectCompleto | undefined,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handlePayload: (data: Evaluation | stClient) => {},
  unfilledSteps: [] as string[],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setIsLoad: (value: boolean) => {},
  load: false,
});

export const EvaluationProject: React.FC<RouteComponentProps<{ id?: string }>> = ({
  match,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  history,
}) => {
  const eventBus = useEventBus();

  useEffect(() => {
    eventBus.dispatchEvent('onChangeTitle', 'Avaliação de projetos');
  }, []);

  const id = useMemo(() => match.params.id, [match.params]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [enableConclusion, setEnableConclusion] = useState(false);
  const payloadRef = useRef(new Evaluation());
  const [hasUpdated, setHasUpdated] = useState(Date.now());
  const [results, setResults] = useState<IEvaluationProjectCompleto>();
  const [isLoading, setIsLoading] = useState(false);
  const [completed, setCompleted] = useState<any>(false);
  const [load, setIsLoad] = useState<boolean>(false);

  const unfilledStepsRef = useRef([
    '1 - Dados Pessoais',
    '2 - Natureza dos dados',
    '3 - Propósitos',
    '4 - Aspectos do tratamento',
    '5 - Agentes Envolvidos',
    '6 - Impactos',
    '7 - Aspectos Técnicos',
  ]);
  const { payload, unfilledSteps } = useMemo(
    () => ({
      payload: payloadRef.current,
      unfilledSteps: unfilledStepsRef.current,
    }),
    [payloadRef.current, unfilledStepsRef.current, hasUpdated],
  );

  const handlePayload = useCallback((data: Evaluation | stClient) => {
    if (typeof data === 'function') {
      const dt = data(payloadRef.current);
      payloadRef.current = dt;
    } else {
      payloadRef.current = { ...payloadRef.current, ...data };
    }
    setHasUpdated(Date.now());
  }, []);

  async function submit(formData: Evaluation, step: string, complete: boolean = false) {
    setIsLoad(true);
    if (complete && unfilledStepsRef.current.length > 1) {
      if (id) {
        try {
          const { data } = await readEvaluationDetail(id);
          setEnableConclusion(data.completed || false);
          setTimeout(() => setEnableConclusion(false), 3000);
          return true;
        } catch (err) {}
      }
    }

    try {
      openNotification('Salvando...', 'info');

      const pay = {
        ...payloadRef.current,
        ...formData,
        jsonCustomObs: JSON.stringify(payloadRef.current.jsonCustomObsObj),
      };

      const { data } = await save(pay);

      const finishForm = Number(step.slice(0, 1));

      if (data.id !== undefined) {
        const response = await getEvaluationFinishPosition(finishForm, data.id);
        if (response.data) {
          unfilledStepsRef.current = unfilledStepsRef.current.filter(i => i !== step);
        }
      }

      handlePayload({ ...pay, id: data.id });
      if (complete) {
        if (data.id) {
          openNotification('Concluindo...', 'info');
          const d = await readEvaluation(data.id);

          if (d.data) {
            setResults(d.data);
            try {
              await sendEmail(data.id);
              openNotification('Concluído com sucesso', 'success');
              setEnableConclusion(true);
            } catch (err) {}
          } else {
            openNotification('Erro ao concluir avaliação', 'error');
          }
        } else {
          openNotification('Erro ao concluir avaliação', 'error');
        }
      } else {
        openNotification('Salvo com sucesso', 'success');
      }
      setIsLoad(false);
      return true;
    } catch (error) {
      setIsLoad(false);
    }
  }

  const accordionsItems: IAccordion[] = [
    {
      title: 'Dados Pessoais',
      // eslint-disable-next-line
      body: props => <PersonalData {...props} />,
      submit: true,
    },
    {
      title: 'Natureza dos dados',
      // eslint-disable-next-line
      body: props => <DataNature {...props} submit={submit} />,
      submit: true,
      // disabled: false,
    },
    {
      title: 'Propósitos',
      // eslint-disable-next-line
      body: props => <Purposes {...props} />,
      submit: true,
      // disabled: false,
    },
    {
      title: 'Aspectos do tratamento',
      // eslint-disable-next-line
      body: props => <TreatmentAspects {...props} />,
      submit: true,
      // disabled: false,
    },
    {
      title: 'Agentes Envolvidos',
      // eslint-disable-next-line
      body: props => <Agents {...props} submit={submit} />,
      submit: true,
      // disabled: false,
    },
    {
      title: 'Impactos',
      // eslint-disable-next-line
      body: props => <Impacts {...props} />,
      submit: true,
      // disabled: false,
    },
    {
      title: 'Aspectos Técnicos',
      // eslint-disable-next-line
      body: props => <TechnicalAspects {...props} />,
      submit: true,
      // disabled: false,
    },
    {
      title: 'Conclusões',
      // eslint-disable-next-line
      body: props => <Conclusions {...props} unfilledSteps={unfilledSteps} submit={submit} />,
      submit: false,
      // disabled: !completed,
    },
  ];

  useEffect(() => {
    if (id) {
      (async () => {
        setIsLoading(true);
        setIsLoad(true);
        const { data } = await readEvaluationDetail(id);
        setCompleted(data.completed);
        const { data: detailData } = await readEvaluation(id);
        setResults(detailData);
        unfilledStepsRef.current = [];
        handlePayload({
          ...data,
          status: data.status ? data.status + '' : undefined,
          jsonCustomObsObj: JSON.parse(data.jsonCustomObs),
        });
        setIsLoading(false);
        setIsLoad(false);
      })();
    }
  }, []);

  return (
    <Box w="100%">
      <Box m="0 auto" maxW="50rem" width="100%">
        <evaluationCtx.Provider
          value={{
            data: payload,
            isDetails: !!id,
            handlePayload,
            results,
            unfilledSteps,
            load,
            setIsLoad,
          }}
        >
          {isLoading ? (
            <Loading />
          ) : (
            <MultipleAccordion
              allowMultiple={completed}
              accordions={accordionsItems}
              runOnChange={submit}
              completed={completed}
            />
          )}
        </evaluationCtx.Provider>
      </Box>
    </Box>
  );
};
